<script lang="ts">
  import { assets } from '$app/paths';
  import slugify from 'slugify';

  import {
    hasHashPattern,
    isYes,
    makeBackgroundValue,
    sortByFields,
  } from './utils/index';
  import type { CountryRow } from './types/index';

  let {
    data = [],
    id,
    backgroundField,
    pattern,
  }: {
    data?: CountryRow[];
    id: string;
    backgroundField?: string;
    pattern?: string;
  } = $props();

  const yesCountries = $derived(
    sortByFields(
      data.filter((row) => isYes(row[id])),
      backgroundField,
      pattern
    )
  );
  const noCountries = $derived(
    sortByFields(
      data.filter((row) => !isYes(row[id])),
      backgroundField,
      pattern
    )
  );
  sortByFields(
    data.filter((row) => !isYes(row[id])),
    backgroundField,
    pattern
  );
</script>

{#if data.length > 0}
  <div
    class="binary-table"
    style={` --icon: url( ${assets}/images/us-base.png)`}
  >
    <div class="binary-table__columns">
      <div
        class="binary-table__col binary-table__col--yes"
        aria-label="countries"
      >
        <div class="binary-table__label h4">Yes</div>
        <ul class="binary-table__list">
          {#each yesCountries as country}
            <li
              class="country-row {slugify(country.country, { lower: true })}"
              class:country-row--hashed={hasHashPattern(country, pattern)}
              style={`--country-row-bg: ${makeBackgroundValue(
                backgroundField ? country[backgroundField] : undefined
              )};`}
            >
              <span class="fi fis fi-{country['iso-code'].toLowerCase()}"
              ></span>
              <span>{country.country}</span>
            </li>
          {/each}
        </ul>
      </div>

      <div
        class="binary-table__col binary-table__col--no"
        aria-label="No countries"
      >
        <div class="binary-table__label h4">No</div>
        <ul class="binary-table__list">
          {#each noCountries as country}
            <li
              class="country-row {slugify(country.country, { lower: true })}"
              class:country-row--hashed={hasHashPattern(country, pattern)}
              style={`--country-row-bg: ${makeBackgroundValue(
                backgroundField ? country[backgroundField] : undefined
              )};`}
            >
              <span class="fi fis fi-{country['iso-code'].toLowerCase()}"
              ></span>
              <span>{country.country}</span>
            </li>
          {/each}
        </ul>
      </div>
    </div>
  </div>
{/if}

<style lang="scss">
  @use '@reuters-graphics/graphics-components/dist/scss/mixins' as mixins;
  @import '/node_modules/flag-icons/css/flag-icons.min.css';

  .fi {
    width: var(--theme-font-size-sm);
    aspect-ratio: 1;
    outline: 1px solid #fff;
    margin-inline-end: 0.25rem;
    border-radius: 3px;
  }

  .binary-table {
    // Set pill colour here
    --table-row-default-bg: #eeeeee;
    --table-list-cols: 3;
    --table-row-height: var(--theme-font-size-xl);
    --table-row-gap: 0.35rem;
    --table-col-gap: 0.35rem;
    --cell-width: 145px;
    --split-gap: 35px;

    // @include mixins.fmt-2;
    // margin: 1rem 0 1.5rem;

    @media (max-width: 1000px) {
      --table-list-cols: 2;
      --split-gap: 25px;
    }

    @media (max-width: 660px) {
      --table-list-cols: 1;
      --split-gap: 15px;
    }
    @media (max-width: 350px) {
      --split-gap: 10px;
    }
  }

  .binary-table__columns {
    display: flex;
    justify-content: left;
  }

  .binary-table__col {
    display: flex;
    flex-direction: column;
    flex: 0 0 auto;
    width: fit-content;
    padding: 0;
    max-width: none;

    @media (max-width: 760px) {
      max-width: none;
    }
  }

  .binary-table__col--yes {
    padding-right: var(--split-gap);
  }

  .binary-table__col--no {
    padding-left: var(--split-gap);
  }

  .h4.binary-table__label {
    font-family: var(--theme-font-family-note);
    // text-transform: none;
    @include mixins.tracking-wide;
    margin: 10px 0 15px 0;
    // color: var(--theme-colour-text-primary);
    @include mixins.text-sm;
    border-bottom: 1px solid #cfcfcf;
    padding-bottom: 2px;
  }

  .binary-table__list {
    display: flex;
    flex-wrap: wrap;
    gap: var(--table-row-gap) var(--table-col-gap);
    width: calc(
      (var(--cell-width) * var(--table-list-cols)) +
        (var(--table-col-gap) * (var(--table-list-cols) - 1))
    );
    list-style: none;
    margin: 0;
    padding: 0;
  }

  .binary-table__col--yes .binary-table__list {
    margin-right: auto;
  }

  .binary-table__col--no .binary-table__list {
    margin-left: auto;
  }

  .country-row {
    // background: var(--country-row-bg, var(--table-row-default-bg));
    background: var(--table-row-default-bg);
    border: 1px solid var(--tr-muted-grey);
    display: flex;
    align-items: center;
    flex: 0 0 var(--cell-width);
    min-width: var(--cell-width);
    max-width: var(--cell-width);
    border-radius: 5px;
    color: var(--theme-colour-text-primary);
    font-family: var(--theme-font-family-note);
    margin: 0;
    min-height: var(--table-row-height);
    overflow: hidden;
    padding: 2px 7px;
    position: relative;
    @include mixins.text-sm;

    &.us {
      // border: 2px solid #666;
    }
  }

  .country-row--hashed::after {
    // background-image: repeating-linear-gradient(
    //   -45deg,
    //   color-mix(in srgb, #777777 12%, transparent) 0 4px,
    //   transparent 4px 8px
    // );
    // content: '';
    // inset: 0;
    // pointer-events: none;
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    content: '';
    display: inline-block;
    width: 1rem;
    height: 1rem;
    background-image: var(--icon);
    background-size: contain;
    background-repeat: no-repeat;
    vertical-align: middle;
  }

  .country-row span {
    position: relative;
    z-index: 1;
  }
</style>
